import { isEqual } from 'lodash';
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Button from '@mui/material/Button';
import MUILink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { GridColDef } from '@mui/x-data-grid-pro';

import DataGridTable from '@/shared/components/dataDisplay/DataGridTable/DataGridTable';
import Action from '@/shared/components/dataDisplay/DataGridTable/components/ActionButton/types/action.type';
import { AppPath } from '@/shared/constants/path';
import TableInstance from '@/shared/constants/tableInstance';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import DialogName from '@/shared/types/dialogName.enum';
import Queue from '@/shared/types/fulfilment/queue.type';
import Order from '@/shared/types/order/order.type';

import CancelConfirmationDialog from './components/CancelConfirmationDialog';
import CancelSubmitToFulfilmentDialog from './components/CancelSubmitToFulfilmentDialog';
import FulfilCustomColumn from './components/FulfilCustomColumn';
import PushToQueueDialog from './components/PushToQueueDialog';
import SubmitToFulfilment from './components/SubmitToFulfilmentDialog/SubmitToFulfilment';
import useFulfilmentQueueTable_v2 from './hooks/useFulfilmentQueueTable_v2';
import useTableActions from './hooks/useTableActions';

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type FulfilmentQueueTableProps = {
  order: Order;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  getTableStateRows: (value: Queue[]) => void;
  setRefetchOrder?: (value: boolean) => void;
};

const FulfilmentQueueTable: FC<FulfilmentQueueTableProps> = ({
  order,
  onUpdateNotificationBar,
  getTableStateRows,
}) => {
  const { t } = useTranslation(['orders', 'common']);

  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();

  const [rowActionsDetails, setRowActionsDetails] = useState<Queue | null>(
    null,
  );

  const [selectedRows, setSelectedRows] = useState<unknown[] | null>(null);
  const selectedRowsRef = useRef<unknown[] | null>(null);

  const {
    apiRef,
    model,
    table,
    columns,
    visibilityModel,
    setVisibilityModel,
    setTable,
    setRefreshTableIndex,
  } = useFulfilmentQueueTable_v2({
    orderId: order.in_order_id,
    getTableStateRows,
  });

  const {
    handleFulfilmentPack,
    handleFulfilmentPick,
    handleFulfilQtyUpdate,
    handlePauseResume,
    handleScheduleQueues,
    handleSubmitQueues,
    handleCancelQueues,
    handlePushToQueue,
  } = useTableActions({
    onUpdateDialogState,
    onUpdateNotificationBar,
    setTable,
    setRefreshTableIndex,
  });

  const rowActionsConfig: Extract<Action, { type: 'default' }>[] = [
    {
      type: 'default',
      button: {
        id: 'pick',
        label: 'Pick',
        onClick: (apiRef, rowId) => {
          const row = apiRef.current.getRowModels().get(rowId || '') as Queue;

          void handleFulfilmentPick([row]);
        },
      },
    },
    {
      type: 'default',
      button: {
        id: 'pack',
        label: 'Pack',
        onClick: (apiRef, rowId) => {
          const row = apiRef.current.getRowModels().get(rowId || '') as Queue;

          void handleFulfilmentPack([row]);
        },
      },
    },
    {
      type: 'default',
      button: {
        id: 'pause_resume',
        label: `${t('common:pause')}/${t('common:resume')}`,
        onClick: (apiRef, rowId) => {
          const row = apiRef.current.getRowModels().get(rowId || '') as Queue;
          void handlePauseResume([row]);
        },
      },
    },
    {
      type: 'default',
      button: {
        label: 'Cancel',
        onClick: (apiRef, rowId) => {
          const row = apiRef.current.getRowModels().get(rowId || '') as Queue;
          onUpdateDialogState(DialogName.CancelQueues, {
            data: [row],
            open: true,
            errors: [],
          });
        },
      },
    },
    {
      type: 'default',
      button: {
        label: 'Schedule',
        onClick: (apiRef, rowId) => {
          const row = apiRef.current.getRowModels().get(rowId || '') as Queue;
          void handleScheduleQueues([row]);
        },
      },
    },
    {
      type: 'default',
      button: {
        label: t('orders:submitToFulfilment'),
        onClick: (apiRef, rowId) => {
          const row = apiRef.current.getRowModels().get(rowId || '') as Queue;
          onUpdateDialogState(DialogName.SubmitToFulfilment, {
            open: true,
            data: [row],
            errors: [],
          });
        },
      },
    },
  ];

  const snackBarActionsConfig: Action[] = [
    {
      type: 'dropdown',
      trigger: {
        button: {
          label: 'status update',
          variant: 'text',
        },
      },
      dropdown: [
        {
          button: {
            id: 'pick',
            label: 'Pick',
            onClick: (apiRef) => {
              const rows = [
                ...apiRef.current.getSelectedRows().values(),
              ] as Queue[];

              void handleFulfilmentPick(rows);
            },
          },
        },
        {
          button: {
            id: 'pack',
            label: 'Pack',
            onClick: (apiRef) => {
              const rows = [
                ...apiRef.current.getSelectedRows().values(),
              ] as Queue[];

              void handleFulfilmentPack(rows);
            },
          },
        },
        {
          button: {
            id: 'pause_resume',
            label: `${t('common:pause')}/${t('common:resume')}`,
            onClick: (apiRef) => {
              const rows = [
                ...apiRef.current.getSelectedRows().values(),
              ] as Queue[];

              void handlePauseResume(rows);
            },
          },
        },
        {
          button: {
            label: t('common:cancel'),
            onClick: (apiRef) => {
              const rows = [
                ...apiRef.current.getSelectedRows().values(),
              ] as Queue[];

              onUpdateDialogState(DialogName.CancelQueues, {
                data: rows,
                open: true,
                errors: [],
              });
            },
          },
        },
      ],
    },
    {
      type: 'default',
      button: {
        label: t('common:schedule'),
        disabled: true,
        onClick: (apiRef) => {
          const rows = [
            ...apiRef.current.getSelectedRows().values(),
          ] as Queue[];

          void handleScheduleQueues(rows);
        },
      },
    },
    {
      type: 'default',
      button: {
        label: t('orders:submitToFulfilment'),
        onClick: (apiRef) => {
          const rows = [
            ...apiRef.current.getSelectedRows().values(),
          ] as Queue[];

          onUpdateDialogState(DialogName.SubmitToFulfilment, {
            open: true,
            data: rows,
            errors: [],
            loading: false,
          });
        },
      },
    },
  ];

  const customizedColumns: GridColDef[] = columns.map((column) => {
    switch (column.field) {
      case 'fulfil_qty':
        return {
          ...column,
          renderCell(params) {
            const row = params.row as Queue;
            return (
              <FulfilCustomColumn
                queue={row}
                onBlur={(value, reason) => {
                  void handleFulfilQtyUpdate(
                    { ...row, fulfil_qty: value },
                    reason,
                  );
                }}
              />
            );
          },
        };
      case 'fulfilment_queue_id':
        return {
          ...column,
          renderCell(params) {
            const row = params.row as Queue;
            return (
              <Typography variant='body2'>
                {row.fulfilment_queue_id.split('-')[0]}
              </Typography>
            );
          },
        };
      case 'status':
        return {
          ...column,
          renderCell(params) {
            const row = params.row as Queue;
            if (row.status === 'HELD') {
              return (
                <MUILink
                  component={Link}
                  variant='body2'
                  to={`${AppPath.Inventory}/${row.inventory_id}`}
                  sx={{
                    color: 'error.main',
                    textDecorationColor: 'inherit',
                  }}
                >
                  {row.status}
                </MUILink>
              );
            }

            return <Typography variant='body2'>{row.status}</Typography>;
          },
        };
      default:
        return column;
    }
  });

  const disableCriteriaForRowActions = rowActionsConfig.map((item) => {
    return {
      ...item,
      button: {
        ...item.button,
        disabled:
          rowActionsDetails?.status === 'HELD' &&
          !['pause_resume', 'pick', 'pack'].includes(item.button.id || ''),
      },
    };
  });

  const disableCriteriaForSnackBarActionsConfig = snackBarActionsConfig.map(
    (item) => {
      const isRowHeld = selectedRows
        ? (selectedRows as Queue[]).some((item) => item.status === 'HELD')
        : false;

      if (item.type === 'dropdown') {
        return {
          ...item,
          dropdown: item.dropdown.map((item) => ({
            ...item,
            button: {
              ...item.button,
              disabled:
                isRowHeld &&
                !['pause_resume', 'pick', 'pack'].includes(
                  item.button.id || '',
                ),
            },
          })),
        };
      }

      return item;
    },
  );

  return (
    <div style={{ height: '60vh', width: '100%' }}>
      <DataGridTable
        getRowId={(item) => item.fulfilment_queue_id as string}
        columns={customizedColumns}
        rows={table.data}
        rowCount={table.totalRows}
        loading={table.isLoading}
        columnVisibilityModel={visibilityModel}
        onColumnVisibilityModelChange={setVisibilityModel}
        onRowSelectionModelChange={(selectedRows) => {
          const rows = [...selectedRows.values()];

          if (!isEqual(selectedRowsRef.current, rows)) {
            selectedRowsRef.current = rows;
            setSelectedRows(rows);
          }
        }}
        onCellClick={(params) => {
          setRowActionsDetails(params.row as Queue);
        }}
        customProps={{
          tableInstance: TableInstance.Queues,
          apiRef,
          model,
          toolbar: {
            title: t('orders:fulfilmentQueue'),
            toolbarActions: (
              <>
                <Button
                  variant='contained'
                  size='large'
                  onClick={() => {
                    void handlePushToQueue(order.in_order_id);
                  }}
                >
                  Push to Queue
                </Button>
              </>
            ),
            rowActions: disableCriteriaForRowActions,
          },
          snackBarProps: {
            actions: disableCriteriaForSnackBarActionsConfig,
          },
        }}
      />

      {dialog?.cancelQueues?.open &&
        (dialog?.cancelQueues?.data as Queue[]) && (
          <CancelConfirmationDialog
            errors={dialog.cancelQueues.errors}
            selectedRowsLength={(dialog?.cancelQueues?.data as Queue[]).length}
            onClose={() => {
              onUpdateDialogState(DialogName.CancelQueues, { open: false });
            }}
            proceed={() => {
              const rows = dialog?.cancelQueues?.data as Queue[];
              void handleCancelQueues(rows);
            }}
          />
        )}

      {dialog?.cancelSubmitToFulfilment?.open && (
        <CancelSubmitToFulfilmentDialog
          onClose={() => {
            onUpdateDialogState(DialogName.CancelSubmitToFulfilment, {
              open: false,
            });
          }}
          proceed={() => {
            onUpdateDialogState(DialogName.CancelSubmitToFulfilment, {
              open: false,
            });
            onUpdateDialogState(DialogName.SubmitToFulfilment, { open: false });
          }}
        />
      )}

      {dialog?.pushToQueue?.open && (
        <PushToQueueDialog
          errors={dialog.pushToQueue.errors}
          onClose={() => {
            onUpdateDialogState(DialogName.PushToQueue, { open: false });
          }}
        />
      )}

      {dialog?.submitToFulfilment?.open && (
        <SubmitToFulfilment
          errors={dialog.submitToFulfilment.errors}
          selectedQueues={dialog.submitToFulfilment.data as Queue[]}
          loading={dialog.submitToFulfilment.loading}
          order={order}
          onClose={() => {
            onUpdateDialogState(DialogName.CancelSubmitToFulfilment, {
              open: true,
              errors: [],
            });
          }}
          proceed={(values) => {
            void handleSubmitQueues(values);
          }}
        />
      )}
    </div>
  );
};

export default FulfilmentQueueTable;
